import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../utils/apiConfig';
import * as pdfjsLib from 'pdfjs-dist';

// Configurar worker de PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * ════════════════════════════════════════════════════════════════════════════
 * EDITOR DE POSICIÓN DE FIRMAS - VERSIÓN CON CANVAS Y ZOOM MANUAL
 * ════════════════════════════════════════════════════════════════════════════
 */

// Constantes del PDF (Letter)
const PDF_WIDTH = 612;
const PDF_HEIGHT = 792;

function PlantillaEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [plantilla, setPlantilla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState({ texto: '', tipo: '' });
  
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [paginaActual, setPaginaActual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  
  // Referencias
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isRenderingRef = useRef(false);
  
  // Escala fija controlada por el usuario
  const [scale, setScale] = useState(1.3);
  
  const [firmaSeleccionada, setFirmaSeleccionada] = useState('paciente');
  
  const [firmaConfig, setFirmaConfig] = useState({
    paciente: { habilitada: true, pagina: 0, x: 350, y: 280, ancho: 150, alto: 50 },
    funcionario: { habilitada: true, pagina: 0, x: 350, y: 150, ancho: 150, alto: 50 }
  });

  const [dragging, setDragging] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // Dimensiones del canvas
  const canvasWidth = PDF_WIDTH * scale;
  const canvasHeight = PDF_HEIGHT * scale;

  // ═══════════════════════════════════════════════════════════════════════════
  // CARGAR PLANTILLA
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => { 
    cargarPlantilla(); 
  }, [id]);

  const cargarPlantilla = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/plantillas/${id}`);
      setPlantilla(response.data);
      
      if (response.data.firmaConfig) {
        setFirmaConfig(prev => ({
          paciente: { ...prev.paciente, ...response.data.firmaConfig.paciente },
          funcionario: { ...prev.funcionario, ...response.data.firmaConfig.funcionario }
        }));
      }
      
      setLoading(false);
      
      if (response.data.archivoOriginal) {
        await generarYCargarPdf();
      }
    } catch (error) {
      console.error('Error cargando plantilla:', error);
      setMensaje({ texto: 'Error al cargar la plantilla', tipo: 'error' });
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CARGAR PDF
  // ═══════════════════════════════════════════════════════════════════════════
  const generarYCargarPdf = async () => {
    try {
      setLoadingPdf(true);
      await api.get(`/api/plantillas/${id}/generar-preview`);
      
      const pdfUrl = `${API_BASE_URL}/api/plantillas/${id}/preview-pdf?t=${Date.now()}`;
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      
      setPdfDoc(pdf);
      setTotalPaginas(pdf.numPages);
      console.log(`✅ PDF cargado: ${pdf.numPages} páginas`);
      
    } catch (error) {
      console.error('Error cargando PDF:', error);
      setMensaje({ texto: 'Error al cargar el PDF', tipo: 'error' });
    } finally {
      setLoadingPdf(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERIZAR PÁGINA
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (pdfDoc && canvasRef.current && !isRenderingRef.current) {
      renderizarPagina();
    }
  }, [pdfDoc, paginaActual, scale]);

  const renderizarPagina = async () => {
    if (!pdfDoc || !canvasRef.current || isRenderingRef.current) return;
    
    // Cancelar render anterior
    if (renderTaskRef.current) {
      try {
        await renderTaskRef.current.cancel();
      } catch (e) {}
      renderTaskRef.current = null;
    }
    
    isRenderingRef.current = true;
    
    try {
      const page = await pdfDoc.getPage(paginaActual + 1);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      const viewport = page.getViewport({ scale });
      
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      const renderTask = page.render({
        canvasContext: context,
        viewport: viewport
      });
      
      renderTaskRef.current = renderTask;
      await renderTask.promise;
      
      console.log(`📄 Página ${paginaActual + 1} renderizada. Escala: ${scale.toFixed(2)}`);
      
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Error renderizando:', error);
      }
    } finally {
      isRenderingRef.current = false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GUARDAR
  // ═══════════════════════════════════════════════════════════════════════════
  const guardarConfiguracion = async () => {
    setSaving(true);
    try {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('💾 GUARDANDO CONFIGURACIÓN DE FIRMAS');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('👤 PACIENTE:', firmaConfig.paciente);
      console.log('👨‍⚕️ FUNCIONARIO:', firmaConfig.funcionario);
      console.log('═══════════════════════════════════════════════════════════');
      
      await api.put(`/api/plantillas/${id}/firma-config`, { firmaConfig });
      setMensaje({ texto: '✅ Configuración guardada', tipo: 'success' });
      setTimeout(() => navigate('/plantillas'), 1500);
    } catch (error) {
      console.error('Error guardando:', error);
      setMensaje({ texto: 'Error al guardar', tipo: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ARRASTRE
  // ═══════════════════════════════════════════════════════════════════════════
  const handleMouseDown = useCallback((e, tipo) => {
    e.preventDefault();
    e.stopPropagation();
    
    const cuadroRect = e.currentTarget.getBoundingClientRect();
    dragOffsetRef.current = { 
      x: e.clientX - cuadroRect.left, 
      y: e.clientY - cuadroRect.top 
    };
    
    setDragging(tipo);
    setFirmaSeleccionada(tipo);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !canvasRef.current) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const config = firmaConfig[dragging];
    
    const visualWidth = config.ancho * scale;
    const visualHeight = config.alto * scale;
    
    let visualLeft = e.clientX - canvasRect.left - dragOffsetRef.current.x;
    let visualTop = e.clientY - canvasRect.top - dragOffsetRef.current.y;
    
    // Limitar dentro del canvas
    visualLeft = Math.max(0, Math.min(visualLeft, canvasWidth - visualWidth));
    visualTop = Math.max(0, Math.min(visualTop, canvasHeight - visualHeight));
    
    // Convertir a coordenadas PDF
    const pdfX = Math.round(visualLeft / scale);
    const pdfY = Math.round(PDF_HEIGHT - (visualTop / scale) - config.alto);
    
    const finalX = Math.max(0, Math.min(pdfX, PDF_WIDTH - config.ancho));
    const finalY = Math.max(0, Math.min(pdfY, PDF_HEIGHT - config.alto));
    
    setFirmaConfig(prev => ({
      ...prev,
      [dragging]: { ...prev[dragging], x: finalX, y: finalY }
    }));
  }, [dragging, firmaConfig, scale, canvasWidth, canvasHeight]);

  const handleMouseUp = useCallback(() => setDragging(null), []);

  // ═══════════════════════════════════════════════════════════════════════════
  // POSICIÓN VISUAL
  // ═══════════════════════════════════════════════════════════════════════════
  const getVisualPos = useCallback((tipo) => {
    const config = firmaConfig[tipo];
    return {
      left: config.x * scale,
      top: (PDF_HEIGHT - config.y - config.alto) * scale,
      width: config.ancho * scale,
      height: config.alto * scale
    };
  }, [firmaConfig, scale]);

  const actualizarConfig = (tipo, campo, valor) => {
    setFirmaConfig(prev => ({ 
      ...prev, 
      [tipo]: { ...prev[tipo], [campo]: valor } 
    }));
  };

  const aplicarPreset = (tipo, pos) => {
    const presets = {
      'tl': { x: 72, y: 680 },
      'tr': { x: 390, y: 680 },
      'ml': { x: 72, y: 400 },
      'mr': { x: 390, y: 400 },
      'bl': { x: 72, y: 120 },
      'br': { x: 390, y: 120 }
    };
    if (presets[pos]) {
      setFirmaConfig(prev => ({
        ...prev,
        [tipo]: { ...prev[tipo], ...presets[pos], pagina: paginaActual }
      }));
    }
  };

  useEffect(() => {
    if (mensaje.texto) {
      const t = setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [mensaje]);

  const posPaciente = getVisualPos('paciente');
  const posFuncionario = getVisualPos('funcionario');

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111827' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #374151', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div 
      style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#111827', fontFamily: 'system-ui' }}
      onMouseMove={handleMouseMove} 
      onMouseUp={handleMouseUp} 
      onMouseLeave={handleMouseUp}
    >
      {/* HEADER */}
      <div style={{ 
        height: 50, 
        background: '#1f2937', 
        borderBottom: '1px solid #374151', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        padding: '0 16px',
        flexShrink: 0 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button 
            onClick={() => navigate('/plantillas')} 
            style={{ background: '#374151', border: 'none', color: '#e5e7eb', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >
            ← Volver
          </button>
          <span style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600, maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {plantilla?.nombre}
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Zoom */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#374151', padding: '4px 8px', borderRadius: 6 }}>
            <span style={{ color: '#9ca3af', fontSize: 11, marginRight: 4 }}>🔍</span>
            <button 
              onClick={() => setScale(s => Math.max(0.5, s - 0.2))} 
              style={{ background: '#4b5563', border: 'none', color: '#fff', width: 24, height: 24, borderRadius: 4, cursor: 'pointer', fontSize: 14 }}
            >−</button>
            <span style={{ color: '#f9fafb', fontSize: 12, minWidth: 40, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </span>
            <button 
              onClick={() => setScale(s => Math.min(2.5, s + 0.2))} 
              style={{ background: '#4b5563', border: 'none', color: '#fff', width: 24, height: 24, borderRadius: 4, cursor: 'pointer', fontSize: 14 }}
            >+</button>
          </div>
          
          {/* Paginador */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#374151', padding: '4px 8px', borderRadius: 6 }}>
            <button 
              onClick={() => setPaginaActual(p => Math.max(0, p - 1))} 
              disabled={paginaActual === 0}
              style={{ background: paginaActual === 0 ? '#374151' : '#4b5563', border: 'none', color: '#fff', width: 24, height: 24, borderRadius: 4, cursor: 'pointer', fontSize: 12, opacity: paginaActual === 0 ? 0.5 : 1 }}
            >◀</button>
            <span style={{ color: '#f9fafb', fontSize: 12, minWidth: 50, textAlign: 'center' }}>
              {paginaActual + 1} / {totalPaginas}
            </span>
            <button 
              onClick={() => setPaginaActual(p => Math.min(totalPaginas - 1, p + 1))} 
              disabled={paginaActual >= totalPaginas - 1}
              style={{ background: paginaActual >= totalPaginas - 1 ? '#374151' : '#4b5563', border: 'none', color: '#fff', width: 24, height: 24, borderRadius: 4, cursor: 'pointer', fontSize: 12, opacity: paginaActual >= totalPaginas - 1 ? 0.5 : 1 }}
            >▶</button>
          </div>
        </div>
        
        <button 
          onClick={guardarConfiguracion} 
          disabled={saving}
          style={{ 
            background: 'linear-gradient(135deg, #10b981, #059669)', 
            border: 'none', 
            color: '#fff', 
            padding: '8px 20px', 
            borderRadius: 6, 
            cursor: 'pointer', 
            fontSize: 14, 
            fontWeight: 600,
            opacity: saving ? 0.7 : 1
          }}
        >
          {saving ? '⏳...' : '💾 Guardar'}
        </button>
      </div>

      {/* CONTENIDO */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        
        {/* DOCUMENTO */}
        <div 
          ref={containerRef}
          style={{ 
            flex: '0 0 75%',
            background: '#0f172a',
            overflow: 'auto',
            padding: 20,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}
        >
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {loadingPdf ? (
              <div style={{ 
                width: canvasWidth, 
                height: canvasHeight, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: '#1e293b',
                borderRadius: 8
              }}>
                <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                  <div style={{ width: 40, height: 40, border: '3px solid #374151', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }}></div>
                  Cargando PDF...
                </div>
              </div>
            ) : (
              <canvas 
                ref={canvasRef}
                style={{ 
                  display: 'block',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                  borderRadius: 4,
                  background: '#fff'
                }}
              />
            )}

            {/* FIRMA PACIENTE */}
            {firmaConfig.paciente.habilitada && firmaConfig.paciente.pagina === paginaActual && !loadingPdf && (
              <div
                onMouseDown={(e) => handleMouseDown(e, 'paciente')}
                style={{
                  position: 'absolute',
                  left: posPaciente.left,
                  top: posPaciente.top,
                  width: posPaciente.width,
                  height: posPaciente.height,
                  background: dragging === 'paciente' ? 'rgba(16,185,129,0.5)' : 'rgba(16,185,129,0.25)',
                  border: `2px ${firmaSeleccionada === 'paciente' ? 'solid' : 'dashed'} #10b981`,
                  borderRadius: 6,
                  cursor: dragging === 'paciente' ? 'grabbing' : 'grab',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: dragging === 'paciente' ? 100 : 10,
                  userSelect: 'none',
                  boxShadow: firmaSeleccionada === 'paciente' ? '0 0 0 3px rgba(16,185,129,0.3)' : 'none'
                }}
              >
                <span style={{ fontSize: 16 }}>✍️</span>
                <span style={{ fontSize: 9, fontWeight: 'bold', color: '#10b981' }}>PACIENTE</span>
              </div>
            )}

            {/* FIRMA FUNCIONARIO */}
            {firmaConfig.funcionario.habilitada && firmaConfig.funcionario.pagina === paginaActual && !loadingPdf && (
              <div
                onMouseDown={(e) => handleMouseDown(e, 'funcionario')}
                style={{
                  position: 'absolute',
                  left: posFuncionario.left,
                  top: posFuncionario.top,
                  width: posFuncionario.width,
                  height: posFuncionario.height,
                  background: dragging === 'funcionario' ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.25)',
                  border: `2px ${firmaSeleccionada === 'funcionario' ? 'solid' : 'dashed'} #3b82f6`,
                  borderRadius: 6,
                  cursor: dragging === 'funcionario' ? 'grabbing' : 'grab',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: dragging === 'funcionario' ? 100 : 10,
                  userSelect: 'none',
                  boxShadow: firmaSeleccionada === 'funcionario' ? '0 0 0 3px rgba(59,130,246,0.3)' : 'none'
                }}
              >
                <span style={{ fontSize: 16 }}>✍️</span>
                <span style={{ fontSize: 9, fontWeight: 'bold', color: '#3b82f6' }}>FUNC.</span>
              </div>
            )}
          </div>
        </div>

        {/* PANEL */}
        <div style={{ 
          flex: '0 0 25%', 
          minWidth: 220, 
          maxWidth: 300, 
          background: '#1f2937', 
          borderLeft: '1px solid #374151', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #374151' }}>
            {['paciente', 'funcionario'].map(t => (
              <button 
                key={t} 
                onClick={() => setFirmaSeleccionada(t)} 
                style={{
                  flex: 1, 
                  padding: 12, 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: 12, 
                  fontWeight: 600,
                  background: firmaSeleccionada === t 
                    ? (t === 'paciente' ? 'rgba(16,185,129,0.2)' : 'rgba(59,130,246,0.2)') 
                    : 'transparent',
                  color: firmaSeleccionada === t 
                    ? (t === 'paciente' ? '#10b981' : '#3b82f6') 
                    : '#9ca3af',
                  borderBottom: firmaSeleccionada === t 
                    ? `2px solid ${t === 'paciente' ? '#10b981' : '#3b82f6'}` 
                    : '2px solid transparent'
                }}
              >
                {t === 'paciente' ? '👤 Paciente' : '👨‍⚕️ Func.'}
              </button>
            ))}
          </div>
          
          <div style={{ flex: 1, padding: 14, overflowY: 'auto' }}>
            
            {/* Switch */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #374151' }}>
              <span style={{ color: '#e5e7eb', fontSize: 13 }}>Habilitada</span>
              <div 
                onClick={() => actualizarConfig(firmaSeleccionada, 'habilitada', !firmaConfig[firmaSeleccionada].habilitada)}
                style={{
                  width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
                  background: firmaConfig[firmaSeleccionada].habilitada ? '#10b981' : '#4b5563'
                }}
              >
                <div style={{ 
                  width: 20, height: 20, background: '#fff', borderRadius: '50%', position: 'absolute', top: 2,
                  transform: firmaConfig[firmaSeleccionada].habilitada ? 'translateX(22px)' : 'translateX(2px)',
                  transition: 'transform 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}></div>
              </div>
            </div>

            {/* Posición */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#9ca3af', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>📍 POSICIÓN (PDF)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10 }}>X</label>
                  <input 
                    type="number" 
                    value={firmaConfig[firmaSeleccionada].x} 
                    onChange={(e) => actualizarConfig(firmaSeleccionada, 'x', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 4, padding: 8, color: '#f9fafb', fontSize: 13 }} 
                  />
                </div>
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10 }}>Y (desde abajo)</label>
                  <input 
                    type="number" 
                    value={firmaConfig[firmaSeleccionada].y} 
                    onChange={(e) => actualizarConfig(firmaSeleccionada, 'y', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 4, padding: 8, color: '#f9fafb', fontSize: 13 }} 
                  />
                </div>
              </div>
            </div>

            {/* Tamaño */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#9ca3af', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>📐 TAMAÑO</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10 }}>Ancho</label>
                  <input 
                    type="number" 
                    value={firmaConfig[firmaSeleccionada].ancho} 
                    onChange={(e) => actualizarConfig(firmaSeleccionada, 'ancho', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 4, padding: 8, color: '#f9fafb', fontSize: 13 }} 
                  />
                </div>
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10 }}>Alto</label>
                  <input 
                    type="number" 
                    value={firmaConfig[firmaSeleccionada].alto} 
                    onChange={(e) => actualizarConfig(firmaSeleccionada, 'alto', parseInt(e.target.value) || 0)}
                    style={{ width: '100%', background: '#111827', border: '1px solid #374151', borderRadius: 4, padding: 8, color: '#f9fafb', fontSize: 13 }} 
                  />
                </div>
              </div>
            </div>

            {/* Página */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#9ca3af', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>📄 PÁGINA</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input 
                  type="number" 
                  value={firmaConfig[firmaSeleccionada].pagina + 1} 
                  onChange={(e) => actualizarConfig(firmaSeleccionada, 'pagina', Math.max(0, (parseInt(e.target.value) || 1) - 1))} 
                  min="1" max={totalPaginas}
                  style={{ flex: 1, background: '#111827', border: '1px solid #374151', borderRadius: 4, padding: 8, color: '#f9fafb', fontSize: 13 }} 
                />
                <button 
                  onClick={() => actualizarConfig(firmaSeleccionada, 'pagina', paginaActual)} 
                  style={{ background: '#374151', border: 'none', color: '#e5e7eb', padding: '8px 12px', borderRadius: 4, cursor: 'pointer' }}
                  title="Usar página actual"
                >📍</button>
              </div>
            </div>

            {/* Presets */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#9ca3af', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>⚡ POSICIÓN RÁPIDA</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                {[['↖ Sup-Izq', 'tl'], ['↗ Sup-Der', 'tr'], ['← Med-Izq', 'ml'], ['→ Med-Der', 'mr'], ['↙ Inf-Izq', 'bl'], ['↘ Inf-Der', 'br']].map(([label, pos]) => (
                  <button 
                    key={pos} 
                    onClick={() => aplicarPreset(firmaSeleccionada, pos)}
                    style={{ padding: 8, background: '#374151', border: 'none', borderRadius: 4, color: '#e5e7eb', fontSize: 11, cursor: 'pointer' }}
                  >{label}</button>
                ))}
              </div>
            </div>

            <button 
              onClick={() => setPaginaActual(firmaConfig[firmaSeleccionada].pagina)}
              style={{ width: '100%', padding: 10, background: 'rgba(59,130,246,0.2)', border: '1px solid #3b82f6', borderRadius: 6, color: '#3b82f6', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: 12 }}
            >👁️ Ir a página de firma</button>
            
            <div style={{ background: '#374151', borderRadius: 6, padding: 10, color: '#9ca3af', fontSize: 11, textAlign: 'center' }}>
              💡 Arrastra los cuadros para posicionar. Usa el zoom para ver mejor.
            </div>
          </div>
        </div>
      </div>

      {mensaje.texto && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, padding: '12px 20px', borderRadius: 8, color: '#fff', fontSize: 13, background: mensaje.tipo === 'success' ? '#10b981' : '#ef4444', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', zIndex: 2000 }}>
          {mensaje.texto}
        </div>
      )}
      
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus { border-color: #3b82f6 !important; outline: none; }
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #1f2937; }
        ::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 4px; }
      `}</style>
    </div>
  );
}

export default PlantillaEditorPage;